















































































import {
  computed,
  defineComponent,
  PropType,
  ref,
  watch
} from '@vue/composition-api';
import { AxiosError } from 'axios';
import {
  useUpdateThresholdGroup,
  useCreateThresholdGroup
} from '@/composable/useWorkflowStateThresholdGroup';
import { cloneDeep } from 'lodash';
import { diffDeep, setErrorFromAxiosError } from '@/utils/helpers';
import {
  IWorkflowStateThresholdGroup,
  IWorkflowStateThresholdGroupData,
  WorkflowStateThresholdGroup
} from '@/models';
import { ISystemError } from '@/models/error/error.types';
import { ALForm } from '@/common/component.types';
import { requiredValidator } from '@/utils/validators';

import ErrorAlert from '@/components/common/ErrorAlert.vue';
import ALDialog from '@/components/core/Dialog/ALDialog.vue';
import GroupAutoCompletePicker from '@/components/account/GroupAutoCompletePicker.vue';

export default defineComponent({
  name: 'WorkflowStateThresholdFormDialog',
  components: {
    ALDialog,
    ErrorAlert,
    GroupAutoCompletePicker
  },
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    isEdit: {
      type: Boolean,
      default: false
    },
    thresholdGroup: {
      type: Object as PropType<IWorkflowStateThresholdGroupData | null>,
      default: null
    },
    workflowId: {
      type: Number,
      required: true
    },
    workflowStateId: {
      type: Number,
      required: true
    }
  },
  emits: ['update:dialog'],

  setup(props, { emit }) {
    const localDialog = computed({
      get: () => props.dialog,
      set: value => {
        emit('update:dialog', value);
      }
    });

    const thresholdGroupData = ref<IWorkflowStateThresholdGroupData>({});

    watch(localDialog, value => {
      if (value) {
        resetFormValidation();
        thresholdGroupData.value = !props.isEdit
          ? ({} as IWorkflowStateThresholdGroupData)
          : cloneDeep(props.thresholdGroup!);
      }
    });

    const formRef = ref<ALForm | null>(null);
    const valid = ref(false);

    const subtitle = computed(
      () => `${props.isEdit ? 'Update' : 'Create'} state threshold group`
    );

    function closeDialog() {
      clearErrors();
      localDialog.value = false;
    }

    const {
      updateThresholdGroup,
      isUpdatingThresholdGroup,
      updateThresholdGroupError,
      updateThresholdGroupErrorReset
    } = useUpdateThresholdGroup(props.workflowId, props.workflowStateId);
    const {
      createThresholdGroup,
      isCreatingThresholdGroup,
      createThresholdGroupError,
      createThresholdGroupErrorReset
    } = useCreateThresholdGroup(props.workflowId, props.workflowStateId);

    const localError = ref<ISystemError | null>(null);

    const loading = computed(() =>
      props.isEdit
        ? isCreatingThresholdGroup.value
        : isUpdatingThresholdGroup.value
    );

    const error = computed<ISystemError | null>({
      get() {
        let systemError: ISystemError | null = localError.value;
        const axiosError = props.isEdit
          ? updateThresholdGroupError.value
          : createThresholdGroupError.value;

        if (axiosError) {
          systemError = setErrorFromAxiosError(axiosError as AxiosError);
        }

        return systemError;
      },
      set(value: ISystemError | null) {
        localError.value = value;
      }
    });

    function clearErrors() {
      error.value = null;
      updateThresholdGroupErrorReset();
      createThresholdGroupErrorReset();
    }

    function resetFormValidation() {
      if (formRef.value) {
        formRef.value.resetValidation();
      }
    }

    function validateForm(): boolean {
      let valid = false;
      if (formRef.value) {
        valid = formRef.value.validate();
      }
      return valid;
    }

    async function save() {
      const valid = await validateForm();

      if (valid) {
        let thresholdGroupInstance: IWorkflowStateThresholdGroup | null = null;

        try {
          thresholdGroupInstance = new WorkflowStateThresholdGroup(
            thresholdGroupData.value
          );
        } catch (e) {
          // When constructing the instance before saving the possibility of an error can occur the local error is to store that error instance and display it
          error.value = e as ISystemError;
        }

        if (thresholdGroupInstance != null) {
          const saveAction = props.isEdit
            ? updateThresholdGroup
            : createThresholdGroup;

          let payload = thresholdGroupInstance;
          if (props.isEdit) {
            payload = diffDeep(
              thresholdGroupInstance,
              props.thresholdGroup,
              true
            );
          }

          saveAction(payload, {
            onSuccess: () => {
              closeDialog();
            }
          });
        }
      }
    }

    return {
      localDialog,
      thresholdGroupData,
      formRef,
      valid,
      subtitle,
      loading,
      error,
      closeDialog,
      clearErrors,
      resetFormValidation,
      save,
      requiredValidator
    };
  }
});
